/**
 * Built-in starter templates shown in the gallery alongside any custom
 * templates fetched from the API. Each entry is a pre-filled
 * IndicatorBuilderConfig snapshot the user can apply with one click.
 *
 * Field references match the backend `field_registry` (DSID + field_id):
 *   - DSID 0   Suppliers (SupplierOnboarding rooted)
 *   - DSID 703 Purchase Orders
 *   - DSID 704 Goods Receipt Notes
 *   - DSID 705 Non-Conformance Reports
 *   - DSID 706 Approvals
 */

import type { IndicatorBuilderConfig } from '../../../lib/api/reporting';
import type { BuilderChartType } from './ChartTypePicker';

export interface StarterTemplate {
  id: string;
  name: string;
  description: string;
  chart_type: BuilderChartType;
  /** lucide-react icon name. */
  icon: string;
  config: IndicatorBuilderConfig;
}

export const STARTER_TEMPLATES: StarterTemplate[] = [
  {
    id: 'top-suppliers-by-spend',
    name: 'Top Suppliers by Spend',
    description:
      'Bar chart ranking suppliers by total PO value. Great first report — swap the dataset or add a date filter to focus on a period.',
    chart_type: 'bar',
    icon: 'BarChart3',
    config: {
      x: { dsid: 703, field_id: 3, paging_count: 10 },
      y: { dsid: 703, field_id: 6, stats_type: 0 },
      paging_count: 10,
    },
  },
  {
    id: 'po-status-breakdown',
    name: 'PO Status Breakdown',
    description:
      'Pie chart showing how purchase orders split across statuses (Draft / Issued / Received / Closed).',
    chart_type: 'pie',
    icon: 'PieChart',
    config: {
      x: { dsid: 703, field_id: 2 },
      y: { dsid: 703, field_id: 1, stats_type: 1 },
    },
  },
  {
    id: 'ncr-trend-over-time',
    name: 'NCR Trend Over Time',
    description:
      'Line chart of monthly non-conformance counts. Spot quality regressions early.',
    chart_type: 'line',
    icon: 'LineChart',
    config: {
      x: { dsid: 705, field_id: 3, date_interval: 2 },
      y: { dsid: 705, field_id: 1, stats_type: 1 },
    },
  },
  {
    id: 'grn-on-time-performance',
    name: 'GRN On-Time Performance',
    description:
      'Pie chart of receipts split by on-time / early / late. Quick OTIF health check.',
    chart_type: 'pie',
    icon: 'CircleDot',
    config: {
      x: { dsid: 704, field_id: 10 },
      y: { dsid: 704, field_id: 1, stats_type: 1 },
    },
  },
  {
    id: 'approvals-pending-kpi',
    name: 'Pending Approvals',
    description:
      'Single-number KPI counting suppliers currently under review. Drag onto a dashboard for an at-a-glance signal.',
    chart_type: 'kpi',
    icon: 'Gauge',
    config: {
      y: { dsid: 706, field_id: 1, stats_type: 1 },
      filters: [
        {
          field_dsid: 706,
          field_id: 2,
          operator: 'eq',
          value: { value: 'under_review' },
        },
      ],
    },
  },
  {
    id: 'supplier-performance-grid',
    name: 'Supplier Performance Grid',
    description:
      'Tabular view of every supplier with initial risk level, spend score, and onboarding status.',
    chart_type: 'grid',
    icon: 'Table',
    // All four columns must share a base queryset for the grid resolver to
    // emit a single .values() projection. Field 22 (Initial Risk Level) lives
    // on SupplierOnboarding alongside fields 20/24/21; field 6 (Overall Risk)
    // would force a join against SupplierRiskAssessment and come back empty.
    config: {
      columns: [
        { dsid: 0, field_id: 20 },
        { dsid: 0, field_id: 22 },
        { dsid: 0, field_id: 24 },
        { dsid: 0, field_id: 21 },
      ],
      sort_list: [{ column: 'Spend Score', dir: 'desc' }],
    },
  },
  {
    id: 'open-pos-grid',
    name: 'Open POs',
    description:
      'Every PO that still needs attention — anything not yet closed. Sorted by delivery date so the most urgent rows surface first.',
    chart_type: 'grid',
    icon: 'LayoutGrid',
    config: {
      columns: [
        { dsid: 703, field_id: 1 },  // PO Number
        { dsid: 703, field_id: 3 },  // Supplier Name
        { dsid: 703, field_id: 2 },  // Status
        { dsid: 703, field_id: 6 },  // Total Value
        { dsid: 703, field_id: 5 },  // Delivery Date
      ],
      filters: [
        {
          field_dsid: 703,
          field_id: 2,
          operator: 'not_in',
          value: { values: ['closed'] },
        },
      ],
      sort_list: [{ column: 'Delivery Date', dir: 'asc' }],
      paging_count: 100,
    },
  },
];

export const STARTER_TEMPLATES_BY_ID: Record<string, StarterTemplate> = Object.fromEntries(
  STARTER_TEMPLATES.map((t) => [t.id, t]),
);
